import { Fragment } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { Gallery, GalleryFact, GalleryImage, GalleryPage, GallerySection, coverOf, gallery, imageUrl, licenceText, loadGallery, pageImages } from '../data/gallery';
import { asteroidReminder, bodyReminder, candidatesReminder, dsoReminder, moonReminder } from '../data/islam';
import { N } from '../data/guide';
import { Sheet, onTop, useDialog } from './chrome';
import { closeGallery, closeViewer, findInSky, galleryNav, openGallery, openPage, openViewer, stepViewer, takeReturnFocus } from './galleryNav';
import { openSizes } from './gallery/sizes-link';
import { Icon } from './icons';
import { Reminder } from './Reminder';
import { paramToSel } from './share';

// The Gallery (panel 'gallery'): the finest real photographs of space, each credited. Its home has the newest pictures,
// our solar system (a page for each planet, each of their large moons, the Sun and the smaller worlds, with a timeline
// and a section for each notable feature; a moon's page starts with its facts) and the deep sky. A picture opens full
// screen, where it can be swiped, pinched and zoomed, with its caption and credit. Data: src/data/gallery.ts; the way
// through it: galleryNav.ts; "How big are they?" and "Compare with Earth": gallery/sizes-link.ts.

/** Load the Gallery's words when a screen that needs them opens; `retry` tries again after a failed load. */
function useGallery(): [Gallery | null | 'failed', () => void] {
  const [failed, setFailed] = useState(false);
  const load = () => {
    setFailed(false);
    if (!gallery.peek()) void loadGallery().catch(() => setFailed(true));
  };
  useEffect(load, []);
  return [gallery.value ?? (failed ? 'failed' : null), load];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** When the picture was taken (or, for most deep-sky views, released): the month and year where known, else the year. */
const when = (img: GalleryImage) => {
  const m = /^(\d{4})-(\d{2})/.exec(img.date);
  if (!m) return String(img.year);
  return `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][+m[2] - 1]} ${m[1]}`;
};

/** The short Islamic reminder a picture's name calls for, as its card in the sky would show it. */
function reminderFor(img: GalleryImage, page: GalleryPage | undefined): string | null {
  // On a moon's page, the moon's name (its pictures are found in the sky by its planet's); among the candidate dwarf
  // planets, the one in the picture.
  if (page?.parent) return moonReminder(page.title);
  if (page?.part === 'dwarfs' && !page.body) return candidatesReminder(img.title.match(/\p{Lu}\p{Ll}+/gu) ?? []);
  const sel = img.sel ?? '';
  if (sel.startsWith('dso:')) {
    const d = services.value?.engine.dsos.find((x) => x.id === sel.slice(4));
    return d ? dsoReminder(d) : null;
  }
  if (sel.startsWith('small:')) return asteroidReminder(sel.slice(6));
  const body = sel.startsWith('body:') ? sel.slice(5) : page?.body;
  return body ? bodyReminder(body, '', '') : null;
}

// ---------------------------------------------------------------- the panel
export function GalleryPanel() {
  const [g, retry] = useGallery();
  const nav = galleryNav.value;
  // Leaving the Gallery for another panel (the stars in Islam, say) starts it afresh next time.
  useEffect(() => () => {
    galleryNav.value = { page: null, viewer: null };
    lastPage = null;
  }, []);
  const viewer = nav.viewer;
  const sheet = !viewer || viewer.exit === 'page';
  return (
    <>
      {sheet && (
        <Sheet onClose={closeGallery} size="tall" label={N.gallery} class="gallery-sheet">
          {g === null ? (
            <div class="working"><div class="spinner" /><p>Opening the Gallery…</p></div>
          ) : g === 'failed' ? (
            <div class="gl-failed">
              <p class="empty">The Gallery could not be opened just now. It needs an internet connection the first time.</p>
              <button class="btn-ghost" onClick={retry}>Try again</button>
            </div>
          ) : nav.page && g.pageById.has(nav.page) ? (
            <PageView key={nav.page} g={g} page={g.pageById.get(nav.page)!} />
          ) : (
            <Home g={g} />
          )}
        </Sheet>
      )}
      {viewer && g && g !== 'failed' && <Viewer g={g} />}
    </>
  );
}

/** The sheet's scrolling body, for keeping and restoring where the reader was. */
const bodyOf = (el: Element | null) => el?.closest<HTMLElement>('.sheet-body') ?? null;
let homeScroll = 0;

/** Once the viewer closes, the tile of the picture last shown takes the focus. */
function useReturnFocus(root: { current: HTMLElement | null }) {
  const nav = galleryNav.value;
  useEffect(() => {
    if (nav.viewer || !nav.returnTo) return;
    const el = root.current?.querySelector<HTMLElement>(`[data-gid="${CSS.escape(nav.returnTo)}"]`);
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'nearest' });
    }
    galleryNav.value = { ...galleryNav.peek(), returnTo: undefined };
  }, [nav.viewer, nav.returnTo]);
}

// ---------------------------------------------------------------- home
function Home({ g }: { g: Gallery }) {
  const root = useRef<HTMLDivElement>(null);
  useReturnFocus(root);
  // Back from a page: where the reader was, and the page's card takes the focus.
  useLayoutEffect(() => {
    const b = bodyOf(root.current);
    if (b) b.scrollTop = lastPage ? homeScroll : 0;
    const from = lastPage;
    lastPage = null;
    if (from) root.current?.querySelector<HTMLElement>(`[data-back="${CSS.escape(from)}"]`)?.focus({ preventScroll: true });
  }, []);
  const newest = g.pages.find((p) => p.group === 'newest');
  const solar = g.pages.filter((p) => p.group === 'solar');
  const part = (k: GalleryPage['part']) => solar.filter((p) => (p.part ?? 'planets') === k);
  const deep = g.pages.filter((p) => p.group === 'deep');
  const hl = g.highlights[0];
  const hlPage = hl && g.pageById.get(hl.page);
  const hlSection = hlPage?.sections.find((s) => s.id === hl.section);
  const hlCover = hl && (g.byId.get(hl.cover ?? '') ?? (hlSection ? g.byId.get(hlSection.items[0]) : hlPage && coverOf(g, hlPage)));
  const count = new Set(g.pages.flatMap((p) => p.sections.flatMap((s) => s.items))).size;
  return (
    <div ref={root} class="gl">
      <h2 class="sheet-title">{N.gallery}</h2>
      <p class="sheet-lead">Real photographs of space, from the first pictures of the Moon to Webb’s newest, with the story of each and who took it. {plural(count, 'picture')}.</p>
      {newest && <Strip g={g} page={newest} />}
      {solar.length > 0 && (
        <>
          <h3 class="section-title">Our solar system</h3>
          <p class="gl-lead">The Sun, the planets and their moons, and the smaller worlds that share our corner of space, from the first photographs to today’s spacecraft.</p>
          {hl && hlPage && (
            <button class="gl-highlight" data-back={`highlight:${hlPage.id}`} onClick={() => goPage(hlPage.id, hlSection?.id, `highlight:${hlPage.id}`)}>
              {hlCover && <Thumb img={hlCover} />}
              <span class="gl-highlight-text">
                <span class="gl-highlight-title">{hl.title}</span>
                <span class="gl-highlight-sub">{hl.text}</span>
              </span>
              <Icon name="chevron" size={16} />
            </button>
          )}
          <button class="gl-highlight gl-sizes" onClick={() => openSizes()}>
            <span class="gl-thumb gl-sizes-art" aria-hidden="true">
              <svg viewBox="0 0 76 76"><circle cx="70" cy="44" r="40" /><circle cx="21" cy="54" r="9" /><circle cx="8" cy="60.5" r="2.5" /></svg>
            </span>
            <span class="gl-highlight-text">
              <span class="gl-highlight-title">How big are they?</span>
              <span class="gl-highlight-sub">The planets and their moons beside Earth, to scale</span>
            </span>
            <Icon name="chevron" size={16} />
          </button>
          <Cards g={g} pages={part('planets')} title="The planets" />
          <Cards g={g} pages={part('moons')} title="Moons" lead="From our own Moon outwards: how big each one is, when it was found, and what spacecraft have seen there." />
          <Cards g={g} pages={part('dwarfs')} title="Dwarf planets" lead="Worlds big enough to be round, but not to clear their paths around the Sun: Ceres in the asteroid belt, and Pluto and others beyond Neptune." />
          <Cards g={g} pages={part('worlds')} title="The Sun and smaller worlds" lead="Our star, and the asteroids, comets and icy worlds that circle it, with two visitors from other stars." />
        </>
      )}
      {deep.length > 0 && (
        <>
          <h3 class="section-title">Deep sky</h3>
          <p class="gl-lead">Our own galaxy across the night sky, the clouds where stars are born, the clusters they form and galaxies far beyond.</p>
          {deep.map((p) => <Strip key={p.id} g={g} page={p} />)}
        </>
      )}
      <p class="fine">Each picture shows its credit and licence, with a link to the page it comes from.</p>
    </div>
  );
}

/** One part of our solar system on the home: a card for each page. */
function Cards({ g, pages, title, lead }: { g: Gallery; pages: GalleryPage[]; title: string; lead?: string }) {
  if (!pages.length) return null;
  return (
    <section class="gl-part" aria-label={title}>
      <h4 class="gl-strip-title gl-part-title">{title}</h4>
      {lead && <p class="gl-lead">{lead}</p>}
      <div class="gl-cards">
        {pages.map((p) => (
          <button key={p.id} class="gl-card" data-back={`page:${p.id}`} data-page={p.id} onClick={() => goPage(p.id)}>
            <PageThumb g={g} page={p} />
            <span class="gl-card-title">{p.title}</span>
            {/* A moon's card names its planet; the others say how many pictures they have. */}
            <span class="gl-card-sub tnum">{p.parent ? planetOf(g, p) : pictures(g, p)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** How many pictures a page has (Haumea's has none yet). */
const pictures = (g: Gallery, p: GalleryPage) => {
  const n = pageImages(g, p).length;
  return n ? plural(n, 'picture') : 'No pictures yet';
};

/** A page's picture on its card: its cover, or for a page without pictures a plain tile with no picture in it. */
function PageThumb({ g, page }: { g: Gallery; page: GalleryPage }) {
  const cover = coverOf(g, page);
  if (cover) return <Thumb img={cover} />;
  return <span class="gl-thumb gl-thumb-empty" aria-hidden="true"><Icon name="planet" size={30} stroke={1.4} /></span>;
}

/** The name of a moon's planet (Earth, for the Moon). */
const planetOf = (g: Gallery, p: GalleryPage) => {
  const parent = p.parent ? g.pageById.get(p.parent) : undefined;
  return parent ? parent.body ?? parent.title : '';
};

/** What on the home last opened a page, so that coming back finds the reader's place and gives that the focus. */
let lastPage: string | null = null;
function goPage(id: string, section?: string, from = `page:${id}`) {
  lastPage = from;
  homeScroll = document.querySelector('.gallery-sheet .sheet-body')?.scrollTop ?? 0;
  openPage(id, section);
}

/** A page's first pictures in a row that scrolls sideways, with the way to all of them. */
function Strip({ g, page }: { g: Gallery; page: GalleryPage }) {
  const s = page.sections[0];
  const all = pageImages(g, page);
  const shown = s.items.slice(0, 10).map((id) => g.byId.get(id)!);
  return (
    <section class="gl-strip-wrap" aria-label={page.title}>
      <div class="section-head gl-strip-head">
        <h3 class="gl-strip-title">{page.title}</h3>
        <button class="gl-see-all" data-back={`page:${page.id}`} onClick={() => goPage(page.id)} aria-label={`See all ${all.length} pictures: ${page.title}`}>
          See all <Icon name="chevron" size={13} stroke={2.2} />
        </button>
      </div>
      <div class="gl-strip">
        {shown.map((img, k) => (
          <Tile key={img.id} img={img} onOpen={() => openViewer({ page: page.id, section: s.id, items: s.items, index: k })} />
        ))}
      </div>
    </section>
  );
}

/** A picture fades in over its own colour once it has loaded (at once if it already has). */
const shown = (el: HTMLImageElement | null) => {
  if (el?.complete && el.naturalWidth > 0) el.classList.add('in');
};

function Thumb({ img }: { img: GalleryImage }) {
  return (
    <span class="gl-thumb" style={{ background: img.colour }} aria-hidden="true">
      <img ref={shown} src={imageUrl(img.id, 480)} alt="" width={480} height={480} loading="lazy" decoding="async" onLoad={(e) => (e.currentTarget as HTMLElement).classList.add('in')} />
    </span>
  );
}

function Tile({ img, onOpen }: { img: GalleryImage; onOpen: () => void }) {
  return (
    <button class="gl-tile" data-gid={img.id} onClick={onOpen}>
      <Thumb img={img} />
      <span class="gl-tile-title">{img.title}</span>
    </button>
  );
}

// ---------------------------------------------------------------- a page
function PageView({ g, page }: { g: Gallery; page: GalleryPage }) {
  const root = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  useReturnFocus(root);
  useLayoutEffect(() => {
    const b = bodyOf(root.current);
    const { section: target, focus } = galleryNav.peek();
    const el = target ? root.current?.querySelector<HTMLElement>(`[data-section="${CSS.escape(target)}"]`) : null;
    if (b) b.scrollTop = el ? Math.max(0, el.offsetTop - 64) : 0;
    // Back from a moon (or another page this one links to): its card takes the focus; else the page's title.
    const card = focus ? root.current?.querySelector<HTMLElement>(`[data-page="${CSS.escape(focus)}"]`) : null;
    (card ?? title.current)?.focus({ preventScroll: true });
    card?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);
  const back = galleryNav.value.back;
  const backPage = back && g.pageById.get(back.page);
  const parent = page.parent ? g.pageById.get(page.parent) : undefined;
  const planet = parent && planetOf(g, page);
  const several = (page.facts?.length ?? 0) > 1 || / and |moons/i.test(page.title);
  const moons = g.moonsOf.get(page.id) ?? [];
  const see = (page.see ?? []).map((id) => g.pageById.get(id)).filter((p): p is GalleryPage => !!p);
  const reminder = page.body ? bodyReminder(page.body, '', '')
    : page.parent ? moonReminder(page.title)
    : page.part === 'dwarfs' ? candidatesReminder((page.facts ?? []).map((f) => f.name ?? ''))
    : null;
  return (
    <div ref={root} class="gl">
      <div class="sheet-title gl-page-title">
        {/* Back to the planet's page a moon was opened from (at its moons), else to the Gallery's home. */}
        <button class="gl-back" onClick={() => (back && backPage ? openPage(back.page, back.section, { focus: back.card }) : openPage(null))} aria-label={backPage ? `Back to ${backPage.title}` : `Back to the ${N.gallery}`}>
          <Icon name="chevron" size={20} stroke={2.2} />
        </button>
        <h2 ref={title} tabIndex={-1}>{page.title}</h2>
      </div>
      {parent && planet && (
        <button class="gl-parent" onClick={() => openPage(parent.id, `${parent.id}-moons`, { focus: page.id })}>
          {planet === 'Earth' ? 'Earth’s moon' : `${several ? 'Moons' : 'A moon'} of ${planet}`}
          <Icon name="chevron" size={13} stroke={2.2} />
        </button>
      )}
      {/* One body's facts open its page; several bodies' (Uranus' four moons, the candidates) follow its introduction. */}
      {page.facts?.length === 1 && <Facts facts={page.facts} planet={planet} />}
      <p class="sheet-lead">{page.intro}</p>
      {(page.facts?.length ?? 0) > 1 && <Facts facts={page.facts!} planet={planet} />}
      {page.note && <p class="gl-note"><Icon name="info" size={16} /> <span>{page.note}</span></p>}
      {reminder && <Reminder text={reminder} />}
      {page.sizes && (
        <button class="btn-ghost gl-compare" onClick={() => openSizes(page.sizes)}>
          <Icon name="sizes" size={17} /> Compare with Earth
        </button>
      )}
      <PageStrip g={g} from={page} pages={moons} id={`${page.id}-moons`} title={page.body === 'Earth' ? 'Its moon' : 'Moons'} />
      <PageStrip g={g} from={page} pages={see} id={`${page.id}-see`} title="See also" />
      {page.sections.map((s) => <SectionView key={s.id} g={g} page={page} s={s} />)}
    </div>
  );
}

/** Other pages in a row: a planet's moons, or the page a part of this one moved to (Ceres, from the asteroids). */
function PageStrip({ g, from, pages, id, title }: { g: Gallery; from: GalleryPage; pages: GalleryPage[]; id: string; title: string }) {
  if (!pages.length) return null;
  return (
    <section class="gl-section gl-pages" data-section={id} aria-label={title}>
      <h3 class="section-title">{title}</h3>
      <div class="gl-strip">
        {pages.map((p) => (
          <button key={p.id} class="gl-card" data-page={p.id} onClick={() => openPage(p.id, undefined, { back: { page: from.id, section: id, card: p.id } })}>
            <PageThumb g={g} page={p} />
            <span class="gl-card-title">{p.title}</span>
            {p.part === 'dwarfs' && p.body && <span class="gl-card-sub">Dwarf planet</span>}
          </button>
        ))}
      </div>
    </section>
  );
}

const km = (n: number) =>
  n >= 1e9 ? `${(n / 1e9).toLocaleString('en-GB', { maximumFractionDigits: 1 })} billion km`
  : n >= 1e7 ? `${Math.round(n / 1e6).toLocaleString('en-GB')} million km`
  : `${(n >= 100 ? Math.round(n) : n).toLocaleString('en-GB', { maximumFractionDigits: 1 })} km`;
const percent = (n: number) => `${n >= 1 ? n.toLocaleString('en-GB', { maximumFractionDigits: 1 }) : Number(n.toPrecision(2))}%`;

/** A moon's or a dwarf planet's facts at the top of its page: its size beside Earth's, its discovery, its distance
 * from its planet (or the Sun), and where they come from (each source named once). */
function Facts({ facts, planet }: { facts: GalleryFact[]; planet?: string }) {
  // Each source once, under the name of who publishes it, each page named by what it vouches for ("JPL (diameter,
  // discovery, distance)") or, where every body has its own, by the body ("JPL (Quaoar, Sedna)").
  type Source = GalleryFact['sources'][number] & { who?: string };
  const sources = new Map<string, Source[]>();
  for (const s of facts.flatMap((f) => f.sources.map((x) => ({ ...x, who: f.name })))) {
    const list = sources.get(s.label) ?? [];
    if (!list.some((x) => x.href === s.href)) sources.set(s.label, [...list, s]);
  }
  const unique = (xs: (string | undefined)[]) => xs.every(Boolean) && new Set(xs).size === xs.length;
  const names = (list: Source[]) => (unique(list.map((s) => s.about)) ? list.map((s) => s.about!) : unique(list.map((s) => s.who)) ? list.map((s) => s.who!) : list.map((_, k) => String(k + 1)));
  const link = (s: Source, text: string) => <a key={s.href} href={s.href} target="_blank" rel="noopener">{text}</a>;
  return (
    <div class="gl-facts">
      {facts.map((f, k) => {
        return (
          <div key={f.name ?? k} class="gl-fact-set">
            {facts.length > 1 && f.name && <h3 class="gl-fact-name">{f.name}</h3>}
            <dl class="gl-fact-list">
              {f.diameterKm != null && (
                <div>
                  <dt>Diameter</dt>
                  <dd class="gl-fact-value tnum">{km(f.diameterKm)}</dd>
                  {f.earthPercent != null && <dd class="gl-fact-sub">{percent(f.earthPercent)} of Earth’s{f.dimensionsKm ? ', on average' : ''}</dd>}
                </div>
              )}
              {f.discovered && (
                <div>
                  <dt>Discovered</dt>
                  <dd class="gl-fact-value tnum">{f.discovered}</dd>
                  {/* "by Galileo Galilei", or the research's words where no one person is credited. */}
                  {f.discoverer && <dd class="gl-fact-sub">{/^No\b/.test(f.discoverer) ? f.discoverer : `by ${f.discoverer}`}</dd>}
                </div>
              )}
              {f.distanceKm != null && (
                <div>
                  <dt>From {planet ?? 'the Sun'}</dt>
                  <dd class="gl-fact-value tnum">{km(f.distanceKm)}</dd>
                  <dd class="gl-fact-sub">on average</dd>
                </div>
              )}
              {f.dimensionsKm && (
                <div class="gl-fact-wide">
                  <dt>Shape</dt>
                  <dd class="gl-fact-value tnum">{f.dimensionsKm.map((d) => Math.round(d).toLocaleString('en-GB')).join(' × ')} km</dd>
                  <dd class="gl-fact-sub">its length, width and height</dd>
                </div>
              )}
            </dl>
          </div>
        );
      })}
      {sources.size > 0 && (
        <p class="gl-fact-src">
          Sources: {[...sources].map(([label, list], k) => (
            <Fragment key={label}>
              {k ? '; ' : ''}
              {list.length > 1 ? <>{label} ({names(list).map((name, j) => <Fragment key={list[j].href}>{j ? ', ' : ''}{link(list[j], name)}</Fragment>)})</> : link(list[0], label)}
            </Fragment>
          ))}
        </p>
      )}
    </div>
  );
}

function SectionView({ g, page, s }: { g: Gallery; page: GalleryPage; s: GallerySection }) {
  const imgs = s.items.map((id) => g.byId.get(id)!);
  const open = (k: number) => openViewer({ page: page.id, section: s.id, items: s.items, index: k });
  return (
    <section class="gl-section" data-section={s.id} aria-label={s.title ?? page.title}>
      {s.title && <h3 class="section-title">{s.title}</h3>}
      {s.intro && <p class="gl-intro">{s.intro}</p>}
      {s.layout === 'timeline' ? (
        <ol class="gl-timeline">
          {imgs.map((img, k) => (
            <li key={img.id}>
              <button class="gl-tl-item" data-gid={img.id} onClick={() => open(k)}>
                <span class="gl-tl-year tnum">{img.year}</span>
                <Thumb img={img} />
                <span class="gl-tl-text">
                  <span class="gl-tl-title">{img.title}</span>
                  <span class="gl-tl-sub">{img.telescope}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <div class="gl-grid">
          {imgs.map((img, k) => <Tile key={img.id} img={img} onOpen={() => open(k)} />)}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- the viewer
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
/** How far a small picture (an early spacecraft frame) may be enlarged to fit the screen before it would look blurred. */
const MAX_ENLARGE = 1.5;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

function Viewer({ g }: { g: Gallery }) {
  const v = galleryNav.value.viewer!;
  const ids = v.items;
  const img = g.byId.get(ids[v.index]);
  const page = g.pageById.get(v.page);
  const ref = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const [chrome, setChrome] = useState(true);
  useDialog(ref, true);
  useLayoutEffect(() => ref.current?.focus({ preventScroll: true }), []);
  // A new picture starts unzoomed and centred, with the row of slides back in place (before it is painted).
  const zoom = useRef({ s: 1, x: 0, y: 0 });
  useLayoutEffect(() => {
    zoom.current = { s: 1, x: 0, y: 0 };
    const t = track.current;
    if (t) {
      t.classList.remove('anim');
      t.style.transform = '';
    }
    ref.current?.style.removeProperty('--pull');
    // The words of the new picture start at the top.
    const info = ref.current?.querySelector('.gv-info');
    if (info) info.scrollTop = 0;
  }, [v.index, ids]);

  // Gestures: swipe between pictures, pinch or double-tap to zoom, drag to look around a zoomed picture, pull down to
  // close, tap to show or hide the words. Applied straight to the elements, so moving a finger never re-renders.
  useEffect(() => {
    const st = stage.current, tr = track.current, root = ref.current;
    if (!st || !tr || !root || !img) return;
    const z = zoom.current;
    const pts = new Map<number, { x: number; y: number }>();
    let mode: 'none' | 'swipe' | 'pan' | 'pinch' | 'pull' = 'none';
    let start = { x: 0, y: 0, t: 0, zx: 0, zy: 0 };
    let pinch = { d: 1, s: 1, mx: 0, my: 0, zx: 0, zy: 0 };
    let lastTap = { t: 0, x: 0, y: 0 };
    let tapTimer: ReturnType<typeof setTimeout> | undefined;
    let moved = false;
    const box = () => st.getBoundingClientRect();
    const fit = () => {
      // As the picture is shown: fitted to the stage, but never more than half as large again as its own pixels.
      const r = box(), k = Math.min(r.width / img.w, r.height / img.h, MAX_ENLARGE);
      return { w: img.w * k, h: img.h * k, W: r.width, H: r.height };
    };
    const el = () => st.querySelector<HTMLElement>('.gv-slide.cur .gv-fit');
    const apply = () => {
      const e = el();
      if (e) e.style.transform = z.s === 1 ? '' : `translate(${z.x}px, ${z.y}px) scale(${z.s})`;
      root.classList.toggle('zoomed', z.s > 1);
    };
    const clampPan = () => {
      const f = fit();
      const mx = Math.max(0, (f.w * z.s - f.W) / 2), my = Math.max(0, (f.h * z.s - f.H) / 2);
      z.x = clamp(z.x, -mx, mx);
      z.y = clamp(z.y, -my, my);
    };
    /** Zoom to `s` keeping the point (px, py), measured from the stage's centre, where it is. */
    const zoomAt = (s: number, px: number, py: number) => {
      const ns = clamp(s, 1, 6);
      z.x = px - (ns / z.s) * (px - z.x);
      z.y = py - (ns / z.s) * (py - z.y);
      z.s = ns;
      if (ns === 1) z.x = z.y = 0;
      clampPan();
      const e = el();
      if (e && !reducedMotion()) {
        e.style.transition = 'transform 0.28s cubic-bezier(.32,.72,0,1)';
        setTimeout(() => (e.style.transition = ''), 300);
      }
      apply();
    };
    const fromCentre = (x: number, y: number) => {
      const r = box();
      return { px: x - r.left - r.width / 2, py: y - r.top - r.height / 2 };
    };
    const slideTo = (dir: -1 | 0 | 1) => {
      const w = box().width;
      if (reducedMotion()) {
        tr.style.transform = '';
        if (dir) stepViewer(dir);
        return;
      }
      tr.classList.add('anim');
      tr.style.transform = `translateX(${-dir * (w + 24)}px)`;
      setTimeout(() => {
        if (dir) stepViewer(dir);
        else {
          tr.classList.remove('anim');
          tr.style.transform = '';
        }
      }, 260);
    };
    const settlePull = (close: boolean) => {
      if (close) return closeViewer();
      root.classList.add('settling');
      root.style.removeProperty('--pull');
      setTimeout(() => root.classList.remove('settling'), 260);
    };
    const down = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      st.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 2) {
        const [a, b] = [...pts.values()];
        const { px, py } = fromCentre((a.x + b.x) / 2, (a.y + b.y) / 2);
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: z.s, mx: px, my: py, zx: z.x, zy: z.y };
        if (mode === 'swipe') slideTo(0);
        mode = 'pinch';
        moved = true;
      } else if (pts.size === 1) {
        start = { x: e.clientX, y: e.clientY, t: performance.now(), zx: z.x, zy: z.y };
        mode = 'none';
        moved = false;
      }
    };
    const move = (e: PointerEvent) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (mode === 'pinch' && pts.size >= 2) {
        const [a, b] = [...pts.values()];
        const { px, py } = fromCentre((a.x + b.x) / 2, (a.y + b.y) / 2);
        const ns = clamp((pinch.s * Math.hypot(a.x - b.x, a.y - b.y)) / pinch.d, 1, 6);
        z.x = px - (ns / pinch.s) * (pinch.mx - pinch.zx);
        z.y = py - (ns / pinch.s) * (pinch.my - pinch.zy);
        z.s = ns;
        clampPan();
        apply();
        return;
      }
      const dx = e.clientX - start.x, dy = e.clientY - start.y;
      if (mode === 'none') {
        if (Math.hypot(dx, dy) < 8) return;
        moved = true;
        mode = z.s > 1 ? 'pan' : Math.abs(dx) > Math.abs(dy) ? 'swipe' : dy > 0 ? 'pull' : 'pan';
      }
      if (mode === 'pan') {
        z.x = start.zx + dx;
        z.y = start.zy + dy;
        clampPan();
        apply();
      } else if (mode === 'swipe') {
        const edge = (dx > 0 && v.index === 0) || (dx < 0 && v.index === ids.length - 1);
        tr.style.transform = `translateX(${edge ? dx * 0.3 : dx}px)`;
      } else if (mode === 'pull') {
        root.style.setProperty('--pull', `${Math.max(0, dy)}`);
      }
    };
    const up = (e: PointerEvent) => {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      if (mode === 'pinch') {
        if (pts.size === 1) {
          const [p] = [...pts.values()];
          start = { x: p.x, y: p.y, t: performance.now(), zx: z.x, zy: z.y };
          mode = 'pan';
        } else if (!pts.size) mode = 'none';
        return;
      }
      if (pts.size) return;
      const dx = e.clientX - start.x, dy = e.clientY - start.y, dt = performance.now() - start.t;
      if (mode === 'swipe') {
        const w = box().width, fast = Math.abs(dx) / Math.max(1, dt) > 0.5 && Math.abs(dx) > 30;
        if ((dx < -w * 0.2 || (fast && dx < 0)) && v.index < ids.length - 1) slideTo(1);
        else if ((dx > w * 0.2 || (fast && dx > 0)) && v.index > 0) slideTo(-1);
        else slideTo(0);
      } else if (mode === 'pull') {
        settlePull(dy > 110 || (dy > 40 && dy / Math.max(1, dt) > 0.6));
      } else if (!moved && (e.pointerType === 'mouse' || dt < 400)) {
        // A tap: twice in quick succession zooms in (or back out); once shows or hides the words.
        const now = performance.now();
        if (now - lastTap.t < 320 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30) {
          clearTimeout(tapTimer);
          lastTap = { t: 0, x: 0, y: 0 };
          const { px, py } = fromCentre(e.clientX, e.clientY);
          if (z.s > 1) zoomAt(1, 0, 0);
          else zoomAt(2.5, px, py);
        } else {
          lastTap = { t: now, x: e.clientX, y: e.clientY };
          clearTimeout(tapTimer);
          tapTimer = setTimeout(() => setChrome((c) => !c), 330);
        }
      }
      mode = 'none';
    };
    const cancel = (e: PointerEvent) => {
      pts.delete(e.pointerId);
      if (mode === 'swipe') slideTo(0);
      if (mode === 'pull') settlePull(false);
      mode = 'none';
    };
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const { px, py } = fromCentre(e.clientX, e.clientY);
      const ns = clamp(z.s * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0025)), 1, 6);
      z.x = px - (ns / z.s) * (px - z.x);
      z.y = py - (ns / z.s) * (py - z.y);
      z.s = ns;
      if (ns === 1) z.x = z.y = 0;
      clampPan();
      apply();
    };
    const key = (e: KeyboardEvent) => {
      if (!onTop(root) || e.defaultPrevented) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeViewer();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        if ((e.target as HTMLElement)?.closest?.('a, input')) return;
        e.preventDefault();
        if (z.s > 1) zoomAt(1, 0, 0);
        slideTo(e.key === 'ArrowRight' ? (v.index < ids.length - 1 ? 1 : 0) : v.index > 0 ? -1 : 0);
      } else if (e.key === '+' || e.key === '=') zoomAt(z.s * 1.6, 0, 0);
      else if (e.key === '-') zoomAt(z.s / 1.6, 0, 0);
      else if (e.key === '0') zoomAt(1, 0, 0);
    };
    st.addEventListener('pointerdown', down);
    st.addEventListener('pointermove', move);
    st.addEventListener('pointerup', up);
    st.addEventListener('pointercancel', cancel);
    st.addEventListener('wheel', wheel, { passive: false });
    window.addEventListener('keydown', key);
    return () => {
      clearTimeout(tapTimer);
      st.removeEventListener('pointerdown', down);
      st.removeEventListener('pointermove', move);
      st.removeEventListener('pointerup', up);
      st.removeEventListener('pointercancel', cancel);
      st.removeEventListener('wheel', wheel);
      window.removeEventListener('keydown', key);
    };
  }, [v.index, ids, img]);

  if (!img) return null;
  const s = services.value;
  // Only what the app can show in the sky: a comet or asteroid must be in its lists.
  const found = img.sel && s ? paramToSel(s, img.sel) : null;
  const sel = found && (found.kind !== 'small' || s!.small.find(found.name)) ? found : null;
  const reminder = reminderFor(img, page);
  const lic = licenceText(img.licence);
  const section = page?.sections.find((x) => x.id === v.section);
  // Where the picture sits: the page, and on a solar-system page the section too (Jupiter · The Great Red Spot).
  const kicker = page ? (page.group === 'solar' && section?.title ? `${page.title} · ${section.title}` : page.title) : '';
  const slides = [-1, 0, 1].map((o) => ({ o, img: g.byId.get(ids[v.index + o] ?? '') })).filter((x) => x.img);
  const go = (d: -1 | 1) => {
    zoom.current = { s: 1, x: 0, y: 0 };
    stepViewer(d);
  };
  return (
    <div class={`gv ${chrome ? '' : 'gv-bare'}`} ref={ref} role="dialog" aria-modal="true" aria-label={`${N.gallery}: ${img.title}`} tabIndex={-1}>
      <div class="gv-top">
        <button class="gv-btn" onClick={closeViewer} aria-label="Close"><Icon name="close" size={16} stroke={2.4} /></button>
        <span class="gv-count tnum" aria-hidden="true">{v.index + 1} of {ids.length}</span>
        <button class="gv-btn" onClick={() => setChrome(!chrome)} aria-pressed={!chrome} aria-label={chrome ? 'Hide the words' : 'Show the words'}>
          <Icon name={chrome ? 'expand' : 'info'} size={18} />
        </button>
      </div>
      <div class="gv-stage" ref={stage}>
        <div class="gv-track" ref={track}>
          {slides.map(({ o, img: im }) => (
            <div key={im!.id} class={`gv-slide ${o === 0 ? 'cur' : ''}`} style={{ '--o': o }} aria-hidden={o !== 0 ? 'true' : undefined}>
              <div class="gv-fit" style={{ '--ar': `${im!.w / im!.h}`, '--w': `${im!.w}`, background: im!.colour }}>
                <img ref={shown} src={imageUrl(im!.id, 2048)} alt={o === 0 ? im!.title : ''} width={im!.w} height={im!.h} decoding="async" draggable={false}
                  onLoad={(e) => (e.currentTarget as HTMLElement).classList.add('in')} />
              </div>
            </div>
          ))}
        </div>
        <button class={`gv-nav gv-prev ${v.index === 0 ? 'end' : ''}`} onClick={() => v.index > 0 && go(-1)} aria-disabled={v.index === 0 ? 'true' : undefined} aria-label="Previous picture">
          <Icon name="chevron" size={20} stroke={2.2} />
        </button>
        <button class={`gv-nav gv-next ${v.index === ids.length - 1 ? 'end' : ''}`} onClick={() => v.index < ids.length - 1 && go(1)} aria-disabled={v.index === ids.length - 1 ? 'true' : undefined} aria-label="Next picture">
          <Icon name="chevron" size={20} stroke={2.2} />
        </button>
        {/* The credit stays on the picture, whatever the words panel shows. */}
        <p class="gv-credit-line" aria-hidden={chrome ? 'true' : undefined}>{img.credit}</p>
      </div>
      <p class="sr-only" aria-live="polite">{`Picture ${v.index + 1} of ${ids.length}: ${img.title}`}</p>
      <div class="gv-info">
        <div class="gv-info-in">
          {kicker && <p class="gv-kicker">{kicker}</p>}
          <h2 class="gv-title">{img.title}</h2>
          <p class="gv-meta">{img.telescope} · <span class="tnum">{when(img)}</span></p>
          <p class="gv-credit">Credit: {img.credit}{lic && <> · {lic.href ? <a href={lic.href} target="_blank" rel="noopener">{lic.text}</a> : lic.text}</>}</p>
          <p class="gv-caption">{img.caption}</p>
          {reminder && <Reminder text={reminder} />}
          <div class="gv-actions">
            {sel && (
              <button class="btn-ghost" onClick={() => findInSky(sel)}><Icon name="target" size={16} /> Find it in the sky</button>
            )}
            <a class="btn-ghost" href={img.source} target="_blank" rel="noopener"><Icon name="external" size={16} /> Source</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- elsewhere in the app
/** Learn's "Gallery" row: the newest pictures, and the way into the Gallery. */
export function GalleryStrip() {
  const [g] = useGallery();
  const root = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!g || g === 'failed') return;
    const back = takeReturnFocus();
    const el = back && root.current?.querySelector<HTMLElement>(`[data-gid="${CSS.escape(back)}"]`);
    if (el) setTimeout(() => el.focus({ preventScroll: false }), 0);
  }, [!!g && g !== 'failed']);
  if (!g || g === 'failed') return null;
  const newest = g.pages.find((p) => p.group === 'newest') ?? g.pages[0];
  if (!newest) return null;
  const s = newest.sections[0];
  return (
    <section class="gl-strip-wrap gl-learn" aria-label={N.gallery} ref={root}>
      <div class="section-head gl-strip-head">
        <h3 class="section-title">{N.gallery}</h3>
        <button class="gl-see-all" onClick={() => openGallery()} aria-label={`See all of the ${N.gallery}`}>See all <Icon name="chevron" size={13} stroke={2.2} /></button>
      </div>
      <p class="gl-lead">The newest pictures from Webb, Hubble and the great observatories, with the story behind each.</p>
      <div class="gl-strip">
        {s.items.slice(0, 8).map((id, k) => {
          const img = g.byId.get(id)!;
          return <Tile key={id} img={img} onOpen={() => openViewer({ page: newest.id, section: s.id, items: s.items, index: k, exit: 'learn' })} />;
        })}
      </div>
    </section>
  );
}

/** On a card in the sky: the Gallery's pictures of this object (a planet, the Moon or the Sun opens its page). */
export function GalleryRow({ sel, body }: { sel?: string; body?: string }) {
  const [g] = useGallery();
  const page = useMemo(() => (g && g !== 'failed' && body ? g.pageOfBody.get(body) : undefined), [g, body]);
  const imgs = useMemo(() => (g && g !== 'failed' && sel ? g.bySel.get(sel) ?? [] : []), [g, sel]);
  if (!g || g === 'failed' || (!page && !imgs.length)) return null;
  const n = page ? pageImages(g, page).length : imgs.length;
  const cover = page ? coverOf(g, page) : imgs[0];
  if (!cover) return null;
  const focusRow = (el: HTMLButtonElement | null) => {
    if (el && takeReturnFocus() === 'row') setTimeout(() => el.focus({ preventScroll: false }), 0);
  };
  const open = () => (page ? openGallery(page.id, undefined, 'card') : openViewer({ page: '', section: sel ?? '', items: imgs.map((i) => i.id), index: 0, exit: 'close' }));
  return (
    <button class="go-card gl-row" onClick={open} ref={focusRow}>
      <Thumb img={cover} />
      <span class="result-text">
        <span class="result-label">In the {N.gallery.toLowerCase()}</span>
        <span class="result-sub">{page ? `${plural(n, 'picture')}, from the earliest to the latest` : n === 1 ? cover.title : `${n} pictures, starting with ${cover.title}`}</span>
      </span>
      <Icon name="chevron" size={16} />
    </button>
  );
}
